import React from 'react';
import { Heart } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const STATUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  attention: 'border-amber-200 bg-amber-100 text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/15 dark:text-amber-100',
  next: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-300',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
};

const METRIC_CARD_CLASSES = {
  urgent: 'border-red-200 bg-red-50/90 dark:border-red-500/20 dark:bg-red-500/5',
  attention: 'border-amber-200 bg-amber-100/85 dark:border-amber-500/20 dark:bg-amber-500/10',
  next: 'border-emerald-200 bg-emerald-50/90 dark:border-emerald-500/20 dark:bg-emerald-500/5',
  steady: 'border-slate-200 bg-white/90 dark:border-white/10 dark:bg-white/[0.03]',
};

function StageBadge({ tone, children }) {
  return (
    <Badge className={STATUS_BADGE_CLASSES[tone] || STATUS_BADGE_CLASSES.steady}>
      {children}
    </Badge>
  );
}

function MetricCard({ metric }) {
  return (
    <Card className={`rounded-[1.2rem] border p-4 ${METRIC_CARD_CLASSES[metric.tone] || METRIC_CARD_CLASSES.steady}`}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
        {metric.label}
      </p>
      <p className="mt-3 text-3xl font-semibold text-slate-900 dark:text-white">
        {metric.value}
      </p>
      <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
        {metric.detail}
      </p>
    </Card>
  );
}

function SupportItemCard({ item, onOpenTool }) {
  return (
    <div className={`rounded-[1.15rem] border p-4 ${METRIC_CARD_CLASSES[item.tone] || METRIC_CARD_CLASSES.steady}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-gray-500">
            {item.eyebrow}
          </p>
          <p className="mt-3 text-base font-semibold text-slate-900 dark:text-white">
            {item.title}
          </p>
        </div>
        <StageBadge tone={item.tone}>
          {item.location}
        </StageBadge>
      </div>
      <p className="mt-3 text-sm leading-6 text-slate-700 dark:text-gray-300">
        {item.detail}
      </p>
      {item.action ? (
        <Button
          type="button"
          variant="outline"
          onClick={() => onOpenTool?.(item.action.tab, item.action.roomId, item.action.toolContext)}
          className="mt-4 border-slate-200 bg-white text-slate-900 hover:bg-slate-50 dark:border-white/10 dark:bg-black/20 dark:text-white dark:hover:bg-black/30"
        >
          {item.action.label}
        </Button>
      ) : null}
    </div>
  );
}

export default function SupportDadsRoom({ context }) {
  return (
    <div className="grid gap-4">
      <Card className="border-slate-200 bg-gradient-to-br from-white via-[#fbfcf7] to-[#eff8f2] p-5 dark:border-white/10 dark:bg-gradient-to-br dark:from-[#20232a] dark:via-[#171b20] dark:to-[#11161a]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-700 dark:text-emerald-300">
                Dad Partners snapshot
              </p>
              <h3 className="mt-3 text-2xl font-semibold leading-8 text-slate-900 dark:text-white">
                What matters on this page right now
              </h3>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
                Use this strip for the few numbers that frame Dad Partners today, then go straight to the work that needs a human move.
              </p>
            </div>
            <StageBadge tone={context.statusTone}>
              {context.statusLabel}
            </StageBadge>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {(context.metrics || []).slice(0, 3).map((metric, index) => (
              <MetricCard key={`${metric.label}-${index}`} metric={metric} />
            ))}
          </div>
        </div>
      </Card>

      <Card className={`border p-5 ${METRIC_CARD_CLASSES.attention}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-slate-500 dark:text-gray-500">
              What needs your move
            </p>
            <h4 className="mt-3 text-xl font-semibold text-slate-900 dark:text-white">
              The most important Dad Partners items to deal with today
            </h4>
            <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-gray-300">
              Keep the page focused on the dads, support, or follow-up items that actually need you to decide or act.
            </p>
          </div>
          <div className="rounded-full bg-white/80 p-3 text-slate-700 shadow-sm dark:bg-black/20 dark:text-gray-200">
            <Heart className="h-5 w-5" />
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {(context.needsYourMoveItems || []).length ? (
            (context.needsYourMoveItems || []).map((item) => (
              <SupportItemCard key={item.id} item={item} onOpenTool={context.onOpenTool} />
            ))
          ) : (
            <div className="rounded-[1.15rem] border border-dashed border-slate-200 bg-white/70 px-4 py-8 text-center dark:border-white/10 dark:bg-black/10">
              <p className="text-base font-semibold text-slate-900 dark:text-white">
                No support queue is pressing right now
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-gray-300">
                Nothing is stuck in review, and no obvious support gaps are shouting for immediate attention.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
